// @flow
import React from 'react';
import injectSheet from 'react-jss';
import cx from 'classnames';
import { S, A, DARK } from '~/app/ui/tokens';

type Props = {
  classes: Object,
  value: string,
  options: Array<string | number | { label?: string, value?: string, val?: string }>,
  onChange: Function,
  error: string,
  disabled?: boolean,
  /** The field's name for screen readers and AI assistants. */
  ariaLabel?: string,
}

function normalizeOptions(options) {
  if (!options || !Array.isArray(options)) return [];
  return options.map((option) => {
    if (option == null) return { label: '', value: '' };
    if (typeof option === 'string' || typeof option === 'number') {
      const text = String(option);
      return { label: text, value: text };
    }
    return {
      label: option.label != null ? String(option.label) : String(option.value ?? ''),
      value: String(option.value ?? option.val ?? option.label ?? ''),
    };
  });
}

function Dropdown({
  classes: c, value, error, options, disabled, onChange, ariaLabel
}: Props) {
  const items = normalizeOptions(options);

  return (
    <div
      className={c.container}
    >
      <select
        aria-label={ariaLabel}
        value={value == null ? '' : String(value)}
        className={cx(c.control, disabled && c.disabled)}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
      >
        {items.map(({ label, value: optionValue }) => (
          <option key={`${optionValue}-${label}`} value={optionValue}>
            {label}
          </option>
        ))}
      </select>
      { error &&
        <span className={c.errorMsg}>
          <i className="material-icons">error</i>
          {error}
        </span>
      }
    </div>
  );
}

const styles = {
  container: {
    fontSize: '12px',
    fontWeight: '400',
    color: 'hsl(0, 0%, 40%)',
    padding: '10px',
    [DARK]: { color: S.muted }
  },
  control: {
    border: '1px solid #ccc',
    display: 'inline-block',
    borderRadius: '3px',
    fontSize: '12px',
    letterSpacing: '0',
    fontFamily: '"Montserrat", sans-serif',
    color: 'hsl(0, 0%, 40%)',
    fontWeight: '400',
    height: '32px',
    width: '160px',
    transition: 'background-color .25s',
    cursor: 'pointer',
    padding: '0 10px',
    backgroundColor: 'hsl(0, 0%, 98%)',
    '&:hover': {
      backgroundColor: 'hsl(0, 0%, 95%)',
      boxShadow: '0px 3px 8px 1px hsl(0, 0%, 80%)',
    },
    [DARK]: {
      borderColor: S.fieldLine,
      color: S.fg,
      backgroundColor: S.field,
      '&:hover': { backgroundColor: S.chromeHover, boxShadow: A.pop }
    }
  },
  disabled: {
    '&:hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      cursor: 'not-allowed'
    },
    [DARK]: { '&:hover': { backgroundColor: 'transparent', boxShadow: 'none' } }
  },
  errorMsg: {
    color: 'hsl(360, 100%, 40%)',
    [DARK]: { color: S.red },
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    marginTop: '5px',
    '&> .material-icons': {
      fontSize: '18px',
      marginRight: '5px'
    }
  }
};

export default injectSheet(styles)(Dropdown);
